// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useState, Fragment, useRef } from 'react';

import type {
  PartCatalog,
  PartAttributeDefinitionVariantId,
  PartListPart,
  WithRequiredNotNull,
} from '@evolvemep/api-ts';
import { Flex, Stack, Text, Anchor, Overlay } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { DraggableDivider } from 'components/Mantine/DraggableDivider';
import { isNotNil, isNil } from 'helpers/isNotNil';
import { useMutableSize, type MutableSizeArgs } from 'hooks/useMutableSize';
import { CatalogCategoryNav } from 'modules/Shop/CatalogSetup/CatalogCategoryNav';
import { CatalogPartList } from 'modules/Shop/CatalogSetup/CatalogPartList';
import { CatalogSearch } from 'modules/Shop/CatalogSetup/CatalogSearch';
import { CategoryFilters } from 'modules/Shop/CatalogSetup/CategoryFilters';
import { useCatalogIntrospector } from 'modules/Shop/CatalogSetup/useCatalogIntrospector';
import type { CategoryPath } from 'modules/Shop/CatalogSetup/useCategoryPath';

export type NeedsRefreshItem = 'categories' | 'filters' | 'parts';

export type CatalogSubComponentRenderOpts = {
  partCatalog: PartCatalog;
  categoryPath: CategoryPath;
  selectedCategory: CategoryPath[number] | undefined;
  onPartSelected: (part: PartListPart) => void;
};

export type CatalogIntrospectorProps = {
  partCatalog: PartCatalog | null | undefined;
  onPartSelected?: (part: PartListPart | null) => void;
  rightSideComponent?: ((opts: CatalogSubComponentRenderOpts) => ReactNode) | ReactNode;
  rightPane?: ((opts: CatalogSubComponentRenderOpts) => ReactNode) | ReactNode;
  rightPaneSizeArgs?: Partial<MutableSizeArgs>;
  wrapPartCards?: boolean;
  disablePart?: (part: PartListPart) => boolean;
  /** Component to render on the right side of the PartCard */
  children?: (part: PartListPart) => ReactNode;
  allowEditing?: boolean;
  disabled?: boolean;
  needsRefresh?: NeedsRefreshItem[];
  useQueryParams?: boolean;
  defaultCategory?: CategoryPath[number];
};

const CatalogIntrospectorWrapped = ({
  partCatalog,
  onPartSelected: onPartSelectedParent,
  rightSideComponent,
  rightPane,
  rightPaneSizeArgs,
  children,
  disablePart,
  wrapPartCards = !children,
  allowEditing,
  disabled,
  needsRefresh,
  useQueryParams,
  defaultCategory,
}: WithRequiredNotNull<CatalogIntrospectorProps, 'partCatalog'>) => {
  const [resizing, setResizing] = useState(false);
  const [partFilter, setPartFilter] = useState<PartAttributeDefinitionVariantId[]>([]);

  const [leftPaneRef, setLeftPaneWidth] = useMutableSize<HTMLDivElement>({
    defaultSize: 280,
    min: 280,
    max: 500,
  });
  const [rightPaneRef, setRightPaneWidth] = useMutableSize<HTMLDivElement>({
    defaultSize: 400,
    min: 400,
    max: 700,
    ...rightPaneSizeArgs,
  });
  const [filterRef, setFilterHeight] = useMutableSize<HTMLDivElement>({
    defaultSize: 250,
    type: 'height',
    min: 200,
    max: 500,
  });

  const {
    categoryPath,
    selectedCategory,
    selectedPart,
    loadingCategoryPath,
    loadingCategory,
    loadingCategoryFromPath,
    parts,
    fetchNextPageParts,
    loadingParts,
    onCategorySelected,
    onPartSelected,
  } = useCatalogIntrospector({
    partCatalog,
    onPartSelected: onPartSelectedParent,
    needsRefresh,
    useQueryParams,
    defaultCategory,
    partFilter,
  });

  const scrollToPartRef = useRef<HTMLDivElement>(null);

  return (
    <Flex flex={1} style={{ overflow: 'hidden' }}>
      <Stack className="category-nav" ref={leftPaneRef} pos="relative">
        <Stack flex={1} gap={0} style={{ overflow: 'hidden' }}>
          <Stack flex={1} p="md" gap={0} style={{ overflow: 'hidden' }}>
            {!needsRefresh?.includes('categories') && (
              <CatalogCategoryNav
                partCatalog={partCatalog}
                categoryPath={categoryPath}
                onCategorySelected={onCategorySelected}
                loading={loadingCategoryPath || loadingCategory || loadingCategoryFromPath}
                allowEditing={allowEditing && partCatalog.editable}
                allowCategoryCreation={allowEditing && partCatalog.editable}
              />
            )}
          </Stack>
          <DraggableDivider onDragDivider={(delta) => setFilterHeight((h) => h + delta)} />
          <Stack p="md" ref={filterRef}>
            {!needsRefresh?.includes('filters') && (
              <CategoryFilters
                selectedCategoryId={selectedCategory?.partCategoryId}
                partFilter={partFilter}
                onPartFilterChanged={setPartFilter}
              />
            )}
          </Stack>
        </Stack>
        {disabled && <Overlay bg="white" opacity={0.5} />}
      </Stack>

      <DraggableDivider
        orientation="vertical"
        onDragDivider={(delta) => setLeftPaneWidth((w) => w + delta)}
        onDraggingChange={(dragging) => setResizing(dragging)}
      />

      <Stack miw={350} p="md" flex={1} style={{ overflow: 'hidden' }} pos="relative">
        <Flex justify="space-between" gap="xs">
          <Flex flex={1} maw={400}>
            <CatalogSearch
              partCatalog={partCatalog}
              onPartSelected={(part) => onPartSelected(part, true)}
              onCategorySelected={onCategorySelected}
              w="100%"
            />
          </Flex>
          <Flex gap="sm">
            {typeof rightSideComponent === 'function'
              ? rightSideComponent({ partCatalog, categoryPath, onPartSelected, selectedCategory })
              : rightSideComponent}
          </Flex>
        </Flex>

        <Flex gap="xs" align="center" wrap="wrap" flex={0}>
          {categoryPath.map((category, i) => (
            <Fragment key={category.partCategoryId}>
              <Anchor
                fz="lg"
                c={i < categoryPath.length - 1 ? 'dimmed' : 'dark'}
                fw={500}
                onClick={() => onCategorySelected(category)}
              >
                {category.name}
              </Anchor>
              {i < categoryPath.length - 1 ? <EvolveIcon icon="ArrowRight" color="gray.5" size="xs" /> : null}
            </Fragment>
          ))}
        </Flex>
        <CatalogPartList
          parts={parts}
          selectedCategory={selectedCategory}
          selectedPart={selectedPart}
          onPartClick={onPartSelectedParent ? (part) => onPartSelected(part, false) : undefined}
          disablePart={disablePart}
          fetchNextPage={fetchNextPageParts}
          loading={loadingParts}
          scrollToPartRef={scrollToPartRef}
          wrapCards={wrapPartCards}
          suspendLayout={resizing}
        >
          {children}
        </CatalogPartList>
        {disabled && <Overlay bg="white" opacity={0.5} />}
      </Stack>

      {isNotNil(rightPane) && (
        <>
          <DraggableDivider
            orientation="vertical"
            onDragDivider={(delta) => setRightPaneWidth((w) => w - delta)}
            onDraggingChange={(dragging) => setResizing(dragging)}
          />
          <Stack ref={rightPaneRef} p="md" style={{ overflow: 'hidden' }}>
            {typeof rightPane === 'function'
              ? rightPane({ partCatalog, categoryPath, onPartSelected, selectedCategory })
              : rightPane}
          </Stack>
        </>
      )}
    </Flex>
  );
};

/** Used to navigate the catalog and do "something" with parts. */
export const CatalogIntrospector = ({ partCatalog, ...props }: CatalogIntrospectorProps) =>
  isNil(partCatalog) ? (
    <Flex justify="center" align="center" flex={1}>
      <Text c="dimmed">Select a catalog.</Text>
    </Flex>
  ) : (
    <CatalogIntrospectorWrapped {...props} partCatalog={partCatalog} />
  );
